import React, { useCallback, useEffect, useState } from 'react';

import { TableCell, TableRow } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { addGoalsToGlobalGoalsMap } from 'actions/globalGoalsMapActions';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { unset } from 'lodash';
import { useDispatch } from 'react-redux';
import DISPLAY_COMPONENT_MAP, { StyledRealignIconComp } from 'screens/analytics/tableCellComponents';
import styled, { css } from 'styled-components';
import { StyledGrid, StyledTypography } from 'styles/common';

import { StringModifier, modifyString } from 'constants/copyHelpers';
import { GOAL_ANALYTICS_EVENTS, sendGoalAnalyticsEvents } from 'constants/mixpanelHelper';
import { routesForTracking } from 'constants/routesForTracking';

import { getObjectiveTimeboundFromId } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography17 } from 'uiToolkit/Typography';

import { useGoalAnalytics } from '../../GoalAnalyticsContext';
import NestedObjectiveTable from './NestedObjectiveTable';
import ObjectiveAndGoalsList, { columnList } from './ObjectiveAndGoalsList';

const StyledContainer = styled(StyledGrid)`
  ${(props) =>
    props.isAbsolutePositioned &&
    css`
      position: absolute;
      width: 185%;
      top: 1rem;
    `}
`;

const LoadingChildObjectives = ({ headers }) => (
  <StyledTableRow hasBottomBorder>
    {headers.map((col, index) => (
      <StyledTableCell align="center">
        <StyledContainer
          container
          justify="center"
          alignItems="center"
          // isAbsolutePositioned={index === headers.length - 1}
        >
          <Skeleton width="80%" />
        </StyledContainer>
      </StyledTableCell>
    ))}
  </StyledTableRow>
);

/**
 * @deprecated 02-04-2025
 */
const ObjectiveTableRow = ({
  headers,
  rowObj,
  updateRowData,
  fetchObjectiveData,
  fetchGoalsData,
  objectiveHeaderId,
  isLastElement,
  levelsForWhichLinesToBeDrawn,
  heirarchy = [], // from root to current level of rows.
  refetchData,
  currentPageNumber,
  setExpandedRowIds = () => {},
  expandedRowIds,
  index,
  rows,
  updateRowDataModifiedForArray,
  addNewObjective,
  rowsData,
  setData,
  expandedRowIdsArray,
  selectedTimeBoundPeriodsTeamHub,
  isTeamHubView,
  loadingObjectiveTableData,
  setIsOpenCreateGoalPanelObjectiveTable,
  setTimeboundDetailsNestedObjectiveTable,
  setAfterGoalCreationNestedObjectiveTable,
}) => {
  // return useMemo( () => {

  const dispatch = useDispatch();
  const { setExpandedRowDetailsView } = useGoalAnalytics();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const {
    state: { currentState },
  } = useGoalAnalytics();
  const { selectedTimeBoundPeriods } = currentState;

  const [fetchingObjectives, setFetchingObjectives] = useState(false);
  const { dataObj, level, children, goals } = rowObj;

  // passing parentUuid
  const headerElement = headers.find((head) => head.uuidType === 'OBJECTIVE');
  const parentObjective = dataObj[headerElement?.id];

  const hierarchyArrayToObject = (arr = [], existingObject = {}) => {
    // const exampleObject = {
    //   123: {
    //     index: 5,
    //     children: {
    //       345: { index: 0, children: null },
    //       453: {
    //         index: 5,
    //         children: {
    //           343: { index: 3, children: null }
    //         }
    //       }
    //     }
    //   }
    // };

    const uuidOfFirstElement = arr[0].uuid;

    const result = arr.reduceRight((all, { uuid, index }) => ({ [uuid]: { index, uuid, children: { ...all } } }), {});

    if (existingObject[uuidOfFirstElement]) {
      return {
        [uuidOfFirstElement]: {
          ...existingObject[uuidOfFirstElement],
          children: {
            ...existingObject[uuidOfFirstElement].children,
            ...result[uuidOfFirstElement].children,
          },
        },
      };
    }
    return { [uuidOfFirstElement]: result[uuidOfFirstElement] };
  };

  const fetchObjectiveRowData = async (parentUuid, indexParam) => {
    /* 
        it fetches data which is shown on expansion of the row
        so it can have goals and objectives. 
    */

    try {
      const indexValue = !Number.isNaN(indexParam) ? indexParam : index;
      const rowObject = rows[indexValue];

      setFetchingObjectives(true);

      sendGoalAnalyticsEvents({
        eventName: GOAL_ANALYTICS_EVENTS.expandedObjective,
        page: {
          pageName: routesForTracking.analyticsGoals.pageName,
          flow: 'Activity',
        },
        groupBy: 'Objective',
        objectiveId: parentUuid,
      });

      //   actual fetching part

      const res = await Promise.all([
        fetchObjectiveData(parentUuid)
          .then((res) => res)
          .catch((err) => {
            console.error('Error fetching objective data : ', err);
            dispatch(showErrorSnackbar(err.message || 'Failed to fetch Objectives!'));
          }),
        fetchGoalsData(parentUuid).then((res) => res),
      ]);

      /* 
      appending the data which was fetched into the existing data in state
    */

      if (res[1].length) {
        dispatch(addGoalsToGlobalGoalsMap({ goals: res[1] }));
      }

      const updatedRowObject = {
        ...rowObject,
        children: res[0]
          ? res[0].values.map((rObj) => ({
              dataObj: rObj,
              level: rowObject.level + 1,
              parentUuid,
              children: null,
            }))
          : null,
        goals: res[1].length ? res[1] : null,
      };

      updateRowData(indexValue, updatedRowObject);
    } catch (err) {
      console.error('Error fetching objective table row data (goals and sub-objectives) : ', err);
    } finally {
      setFetchingObjectives(false);
    }
  };

  const [isExpanded, setIsExpanded] = useState(false);
  const [isHoveredOver, setIsHoveredOver] = useState(false);

  const expandRow = (uuid, forceRefetch = false, indexParam) => {
    setIsExpanded(true);
    // to use hierarchy to set it in parent-child relation
    const res = hierarchyArrayToObject(heirarchy, expandedRowIds);
    setExpandedRowIds((prev) => ({
      ...prev,
      ...res,
    }));
    if (forceRefetch) {
      fetchObjectiveRowData(uuid, indexParam);
    }
  };

  const collapseRow = (uuid) => {
    setIsExpanded(false);
    const filteredExpandedIds = expandedRowIds;
    // delete filteredExpandedIds[uuid];

    const deleteString = heirarchy.reduce(
      (previous, current) => (previous === '' ? `${current.uuid}` : `${previous}.children.${current.uuid}`),
      ''
    );

    unset(filteredExpandedIds, deleteString);

    setExpandedRowIds(filteredExpandedIds);

    updateRowData(index, {
      ...rowObj,
      children: null,
      goals: null,
    });
  };

  useEffect(() => {
    if (isExpanded) {
      fetchObjectiveRowData(parentObjective?.uuid);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (expandedRowIdsArray && rowObj && !loadingObjectiveTableData) {
      if (expandedRowIdsArray?.includes(rowObj?.dataObj['1']?.uuid)) {
        setIsExpanded(true);
      }
    }
  }, [expandedRowIdsArray, rowObj, loadingObjectiveTableData]);

  const viewGoals = ({ uuid, displayName }) => {
    sendGoalAnalyticsEvents({
      eventName: GOAL_ANALYTICS_EVENTS.openedExpandedView,
      page: {
        pageName: routesForTracking.analyticsGoals.pageName,
        flow: 'Activity',
      },
      groupBy: 'Objective',
      objectiveId: uuid,
    });

    const headerText = heirarchy.reduce(
      (previous, current) => (previous === '' ? `${current.value}` : `${previous} > ${current.value}`),
      ''
    );

    setExpandedRowDetailsView({
      headerComponent: (
        <StyledTypography variant="h6" weight={600}>
          {headerText}
        </StyledTypography>
      ),
      infoObj: {
        uuid,
      },
    });
  };

  const udpateObjectiveName = (objectiveName) => {
    const dataObj = {
      ...rowObj.dataObj,
      [objectiveHeaderId]: {
        ...rowObj?.dataObj?.[objectiveHeaderId],
        value: objectiveName,
      },
    };

    updateRowData(index, {
      ...rowObj,
      dataObj,
    });
  };

  const [timeboundDetails, setTimeboundDetails] = useState(null);

  //  time bound details id

  const timeBoundDetails = async (id) => {
    try {
      const res = await getObjectiveTimeboundFromId({ objectiveTimeBoundId: id });
      const flattenedRes = {
        ...res,
        ...res.organizationTag,
        id,
        objectiveId: res.organizationTag.uuid,
        objectiveTimeBoundId: id,
        objectiveType: 'GOAL',
      };
      setTimeboundDetails(flattenedRes);
    } catch (err) {
      console.error(`Error in fetching time bound details for time bound id : ${id} , error : `, err);
    }
  };

  useEffect(() => {
    timeBoundDetails(rowObj.dataObj['1'].uuid);
  }, [rowObj]);

  // adding table row open reset on page change
  // useEffect(() => {
  //   setIsExpanded(false);
  // }, [currentPageNumber]);

  useEffect(() => {
    if (expandedRowIdsArray && rowObj && !loadingObjectiveTableData) {
      if (isExpanded && !expandedRowIdsArray?.includes(rowObj?.dataObj['1']?.uuid)) {
        setIsExpanded(false);
      }
    }
  }, [expandedRowIdsArray, rowObj, loadingObjectiveTableData, isExpanded]);

  // todo: for nested tree open , will have to run expandRow func here too

  const handleMouseEnter = useCallback(() => {
    setIsHoveredOver(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHoveredOver(false);
  }, []);

  return (
    <>
      <StyledTableRow isExpanded={isExpanded}>
        {headers.map(({ id, uuidType, status }, index) => (
          <StyledTableCell
            key={`objective-table-cell-${id}`}
            align={uuidType === 'AGGREGATED_METRICS' ? 'center' : 'inherit'}
            borderLeft={index > 0}
            hoverable
            style={{
              overflow: 'visible',
              position: 'relative',
              padding: '0',
            }}
            // $isExpanded={isExpanded}
          >
            {DISPLAY_COMPONENT_MAP[uuidType]({
              ...dataObj[id],
              level,
              children,
              goals,
              summaryTitle: dataObj[objectiveHeaderId]?.value,
              status,
              expandRow,
              collapseRow,
              viewGoals,
              isLastElement,
              levelsForWhichLinesToBeDrawn,
              isExpanded,
              setIsExpanded,
              refetchData,
              currentPageNumber,
              expandedRowIds,
              fetchObjectiveData,
              fetchGoalsData,
              rows: rowsData,
              updateRowDataModifiedForArray,
              addNewObjective,
              isTeamHubView,
              selectedTimeBoundPeriod: isTeamHubView
                ? selectedTimeBoundPeriodsTeamHub
                : (selectedTimeBoundPeriods?.[0] ?? null),
              setIsOpenCreateGoalPanelObjectiveTable,
              setTimeboundDetailsNestedObjectiveTable,
              setAfterGoalCreationNestedObjectiveTable,
              timeboundDetails,
              udpateObjectiveName,
            })}
          </StyledTableCell>
        ))}
      </StyledTableRow>

      {/* 
        adding no goals/objectives on expansion component
        todo: check if adding isExpanded checks in rowObj.goals cause any issues

      */}

      {isExpanded && !fetchingObjectives && !(rowObj?.goals || (rowObj?.children && rowObj?.children?.length)) && (
        <StyledTableCell colSpan="3">
          <Typography17 leftGutter>
            No {modifyString(keyResultsName, StringModifier.SENTENCE_CASE)} or{' '}
            {modifyString(objectivesName, StringModifier.SENTENCE_CASE)} yet
          </Typography17>
        </StyledTableCell>
      )}

      {
        // todo: not sure if this can be added to the tabelCellComponents
        rowObj.goals && isExpanded && (
          <StyledTableRow>
            <StyledTableCell colSpan={headers.length} style={{ padding: 0 }}>
              <ObjectiveAndGoalsList
                loading={fetchingObjectives}
                goals={rowObj.goals}
                level={rowObj.level + 1}
                subObjectivesLength={rowObj.children ? rowObj.children.length : 0}
                isLastObjectivesGoal={isLastElement && rowObj.level === 1}
                levelsForWhichLinesToBeDrawn={levelsForWhichLinesToBeDrawn}
                refetchData={refetchData}
                currentPageNumber={currentPageNumber}
                expandRow={expandRow}
                parentObjective={parentObjective}
                setExpandedRowIds={setExpandedRowIds}
                expandedRowIds={expandedRowIds}
                fetchObjectiveData={fetchObjectiveData}
                fetchGoalsData={fetchGoalsData}
                rows={rowsData}
                updateRowDataModifiedForArray={updateRowDataModifiedForArray}
                isTeamHubView={isTeamHubView}

                // $isExpanded={isExpanded}
              />
            </StyledTableCell>
          </StyledTableRow>
        )
      }

      {fetchingObjectives ? (
        <LoadingChildObjectives headers={columnList} />
      ) : (
        rowObj.children &&
        isExpanded && (
          <NestedObjectiveTable
            headers={headers}
            rows={rowObj.children}
            updateRowData={(rows) =>
              updateRowData(index, {
                ...rowObj,
                children: rows,
              })
            }
            heirarchy={heirarchy}
            fetchObjectiveData={fetchObjectiveData}
            fetchGoalsData={fetchGoalsData}
            objectiveHeaderId={objectiveHeaderId}
            levelsForWhichLinesToBeDrawn={levelsForWhichLinesToBeDrawn}
            refetchData={refetchData}
            currentPageNumber={currentPageNumber}
            expandRow={expandRow}
            parentObjective={parentObjective}
            setExpandedRowIds={setExpandedRowIds}
            expandedRowIds={expandedRowIds}
            rowsData={rowsData}
            setData={setData}
            updateRowDataModifiedForArray={updateRowDataModifiedForArray}
            addNewObjective={addNewObjective}
            selectedTimeBoundPeriodsTeamHub={selectedTimeBoundPeriodsTeamHub}
            isTeamHubView={isTeamHubView}
            setIsOpenCreateGoalPanelObjectiveTable={setIsOpenCreateGoalPanelObjectiveTable}
            setTimeboundDetailsNestedObjectiveTable={setTimeboundDetailsNestedObjectiveTable}
            setAfterGoalCreationNestedObjectiveTable={setAfterGoalCreationNestedObjectiveTable}
          />
        )
      )}
    </>
  );
};

const StyledTableRow = styled(TableRow)`
  th,
  td {
    border-bottom: 1px solid ${(props) => props.theme.palette.borders.lightGrey};
  }
  ${(props) =>
    props.hasBottomBorder &&
    css`
      border-bottom: 1px solid ${props.theme.palette.borders.lightGrey};
    `}
`;

const StyledTableCell = styled(TableCell)`
  position: relative;
  border-left: ${(props) => props.borderLeft && `1px solid ${props.theme.palette.borders.lightGrey}`};
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'default')};

  ${(props) =>
    props.hoverable &&
    css`
      &:hover {
        background-color: ${(props) => props.theme.palette.background.lightBlue};
        ${StyledRealignIconComp} {
          visibility: visible;
        }
      }
    `}
`;

export default ObjectiveTableRow;
